import { InputError } from "../../components/Input/types"
import { closeModal } from "../../contexts/ModalContext/actions"
import i18n from "../../i18n"
import { addMobileToFleet, createFleet, removeMobileFromFleet, updateFleet } from "../../services/calls/fleet"
import { updateMobile } from "../../services/calls/mobiles"
import { createSensorTemperatureSettings, createSensorTemperatureSettingsContact, deleteSensorTemperatureSettingsContact, updateSensorTemperatureSettings } from "../../services/calls/sensors"
import { addUserFleetPermission, createSystemUser, removeUserFleetPermission, updateSystemUser } from "../../services/calls/users"
import { objToFormData } from "../../services/utils/convert"
import { findArrayChange, isValue } from "../../services/utils/misc"
import { isValidEmail } from "../../services/utils/validation"

export const saveTemperatureSettings = async (data: any, id: number) => {
  let isValid = true
  const error: Record<string, InputError> = {}

  if (data?.emails?.length && isValid) {
    data.emails.forEach((email: string) => {
      if (!isValid) return false
      isValid = isValidEmail(email)
    })

    if (!isValid) {
      error.emails = {
        type: 'error',
        message: i18n.t('Not all emails are valid')
      }
    }
  }

  if (!isValue(data.sensor)) {
    isValid = false
    error.sensor = {
      type: 'error',
      message: i18n.t('Sensor is required')
    }
  }

  if (isValid) {
    if (id) {
      await updateSensorTemperatureSettings(id, data)

      const emailChange = findArrayChange(data.emails, data.original.emails)
      if (emailChange.add.length) {
        emailChange.add.forEach((email) => {
          createSensorTemperatureSettingsContact({ temperature_settings: id, email })
        })
      }
      if (emailChange.remove.length) {
        emailChange.remove.forEach((email) => {
          const setting = data.contactMap.find((contact) => contact.email === email)
          if (setting) deleteSensorTemperatureSettingsContact(setting.id)
        })
      }

      const smsChange = findArrayChange(data.sms, data.original.sms)
      if (smsChange.add.length) {
        smsChange.add.forEach((sms) => {
          createSensorTemperatureSettingsContact({ temperature_settings: id, sms })
        })
      }
      if (smsChange.remove.length) {
        smsChange.remove.forEach((sms) => {
          const setting = data.contactMap.find((contact) => contact.sms === sms)
          if (setting) deleteSensorTemperatureSettingsContact(setting.id)
        })
      }
    } else {
      const newSettings = await createSensorTemperatureSettings(data)

      if (data.emails.length) {
        data.emails.forEach((email: string) => {
          createSensorTemperatureSettingsContact({ temperature_settings: newSettings.id, email })
        })
      }

      if (data.sms.length) {
        data.sms.forEach((sms: string) => {
          createSensorTemperatureSettingsContact({ temperature_settings: newSettings.id, sms })
        })
      }
    }

    closeModal()
    return {
      status: 'success'
    }
  }

  return {
    status: 'error',
    error
  }
}

export const saveSystemUser = async (data: any, id: number, oldData: any) => {
  let isValid = true
  const error: Record<string, InputError> = {}

  if (!data.username) {
    isValid = false
    error.username = {
      type: 'error',
      message: i18n.t('Username is required')
    }
  } else if (id && !isValidEmail(data.username) && isValidEmail(oldData.username)) {
    // If username was an email, then it should not be changed
    isValid = false
    error.username = {
      type: 'error',
      message: i18n.t('Username is should be a valid email')
    }
  }

  if (isValid) {
    if (id) {
      await updateSystemUser(id, data)
      const oldFleets = oldData.fleets?.map((fleet) => fleet.fleetid)

      const fleetChange = findArrayChange(data.fleets, oldFleets)
      if (fleetChange.add?.length) {
        fleetChange.add.forEach((fleet) => {
          addUserFleetPermission(id, fleet)
        })
      }
      if (fleetChange.remove?.length) {
        fleetChange.remove?.forEach((key) => {
          const { fleetid } = oldData.fleets.find((fleet) => fleet.fleetid === key)
          removeUserFleetPermission(id, fleetid)
        })
      }
    } else {
      const newUser = await createSystemUser({ ...data, email: data.username })
      if (data.fleets) {
        data.fleets.add.forEach((fleet) => {
          addUserFleetPermission(newUser.id, fleet)
        })
      }
    }

    closeModal()
    return {
      status: 'success'
    }
  }

  return {
    status: 'error',
    error
  }
}

export const saveFleet = async (data: any, id: number, oldData: any) => {
  let isValid = true
  const error: Record<string, InputError> = {}

  if (!data.name) {
    isValid = false
    error.name = {
      type: 'error',
      message: i18n.t('Name is required')
    }
  }

  if (isValid) {
    if (id) {
      await updateFleet(id, data)
      const oldMobileArr = oldData.mobiles?.map((mobile) => mobile.id)

      // Go over the mobiles within the fleet and see which were added and which were removed
      const mobileChange = findArrayChange(data.mobiles, oldMobileArr)
      if (mobileChange.add?.length) {
        mobileChange.add.forEach((mobile) => {
          addMobileToFleet(id, mobile)
        })
      }
      if (mobileChange.remove.length) {
        mobileChange.remove?.forEach((key) => {
          const { owner } = oldData.mobiles.find((mobile) => mobile.id === key)
          removeMobileFromFleet(owner)
        })
      }
    } else {
      const newFleet = await createFleet(data)
      if (data.mobiles) {
        data.mobiles.forEach((mobile) => {
          addMobileToFleet(newFleet.fleetid, mobile)
        })
      }
    }

    closeModal()
    return {
      status: 'success'
    }
  }

  return {
    status: 'error',
    error
  }
}

export const saveMobile = async (data: any, id: number, oldData: any) => {
  let isValid = true
  const error: Record<string, InputError> = {}

  if (!isValue(data.globalid)) {
    isValid = false
    error.globalid = {
      type: 'error',
      message: i18n.t('Global ID is required')
    }
  }

  if (isValid) {
    if (id) {
      const tempData = { ...data }
      delete tempData.registration_number // Delete as it's a read only field and can't be changed
      if (tempData.picture.length && typeof tempData.picture[0] !== 'string') {
        tempData.image_upload = tempData.picture[0] // Doesn't need to be an array and only 1 image allowed
      }
      if (tempData.cust_icon === oldData.cust_icon) delete tempData.cust_icon
      else if (tempData.cust_icon !== oldData.cust_icon && typeof tempData === 'object') {
        const { color, icon } = tempData.cust_icon
        tempData.cust_icon = `${icon}_${color}`
      }

      const formData = objToFormData(tempData)
      await updateMobile(id, formData)
      const oldMobileArr = oldData.all_fleets?.map((fleet) => fleet.fleet_id)

      // Go over the fleet access to see what has changed
      const currFleets = data.all_fleets.map((fleet) => fleet.fleet_id)
      const accessChange = findArrayChange(currFleets, oldMobileArr)
      if (accessChange.add?.length) {
        accessChange.add.forEach((fleet) => {
          addMobileToFleet(fleet, id)
        })
      }
      if (accessChange.remove.length) {
        accessChange.remove?.forEach((key) => {
          const { owner_id } = oldData.all_fleets.find((fleet) => fleet.id === key)
          removeMobileFromFleet(owner_id)
        })
      }
    }

    closeModal()
    return {
      status: 'success'
    }
  }

  return {
    status: 'error',
    error
  }
}
